import { faXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dialog as DialogPrimitive } from "radix-ui"
import type { ComponentProps } from "react"
import { cn } from "cn"

function Dialog({ ...props }: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({
  ...props
}: ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

/** Figma-Achse „Size" bestimmt nur die Breite, alle übrigen Stile bleiben gleich. */
const DIALOG_CONTENT_SIZES = {
  sm: "w-[448px]",
  md: "w-[680px]",
  lg: "w-[916px]",
  xl: "w-[1144px]",
} as const

export type DialogSize = keyof typeof DIALOG_CONTENT_SIZES

export type DialogContentProps = ComponentProps<
  typeof DialogPrimitive.Content
> & {
  size?: DialogSize
  /** Figma zeigt den Schließen-Button in jeder Variante fest verbaut. */
  showCloseButton?: boolean
}

function DialogContent({
  className,
  size = "sm",
  showCloseButton = true,
  children,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-size={size}
        className={cn(
          "fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-y-auto rounded-xl bg-backgrounds-primary shadow-[0px_15px_25px_rgba(62,58,50,0.25)] outline-hidden",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          DIALOG_CONTENT_SIZES[size],
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close-button"
            className="absolute top-3 right-2 flex size-6 shrink-0 items-center justify-center rounded p-0.5 text-labels-secondary outline-none focus-visible:ring-2 focus-visible:ring-neutrals-v2-600 focus-visible:ring-offset-2"
          >
            <FontAwesomeIcon icon={faXmark} className="size-5!" />
            <span className="sr-only">Schließen</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

/** Figma „Text": Titel und Beschreibung/Content inkl. Abstand zum Footer. */
function DialogHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex w-full flex-col items-start gap-4 p-6",
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "w-full text-xl leading-7 font-[650] text-labels-primary",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("w-full text-sm leading-5 text-labels-primary", className)}
      {...props}
    />
  )
}

export type DialogFooterVariant = "default" | "sticky"

export type DialogFooterProps = ComponentProps<"div"> & {
  /**
   * Figma „Variante": `sticky` zeigt einen Trenner samt reduziertem
   * vertikalem Abstand (für scrollbaren Content), `default` sitzt ohne
   * Trenner unter dem Inhalt.
   */
  variant?: DialogFooterVariant
}

/** Figma „Footer": CTAs sind `children` (z. B. `Button` aus `@e2n/ui/v2`). */
function DialogFooter({
  className,
  variant = "sticky",
  ...props
}: DialogFooterProps) {
  return (
    <div
      data-slot="dialog-footer"
      data-variant={variant}
      className={cn(
        "flex w-full items-center justify-end gap-2 rounded-b-xl bg-backgrounds-primary px-6",
        variant === "sticky"
          ? "border-t border-separators-non-opaque py-3"
          : "items-start pb-6",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
